import { api } from "../../api";
import { h } from "../shared/safe";
import { pill } from "../shared/blocks";
import { ACTOR_LABELS, describeProblem, fieldLabel, parseHistory, type HistoryEntry, type PageInfo } from "./factsModel";
import { card, whoText } from "./kit";
import { enc, type Workspace } from "./workspace";

// HISTORY: every change to the approved facts, newest first: who (the dashboard, an AI client through MCP, or someone else), when, what
// changed (before and after) and why. The local boundary labels its callers "local-dashboard" and "local-mcp".

const PAGE = 15;

export function historyCard(ws: Workspace): HTMLElement {
  const body = h("div");
  const out = card("fx-history", "History of approved facts", body);
  let entries: HistoryEntry[] | null = null;
  let page: PageInfo | null = null;
  let offset = 0;
  let loadError: string | null = null;
  const prev = h("button", { type: "button", class: "secondary", id: "fx-hist-prev", on: { click: () => void go(offset - PAGE) } }, "← Newer");
  const next = h("button", { type: "button", class: "secondary", id: "fx-hist-next", on: { click: () => void go(offset + PAGE) } }, "Older →");
  const nav = h("nav", { class: "qf-pager", "aria-label": "History pages" }, prev, h("span", { class: "qf-pager__note", id: "fx-hist-range", role: "status", "aria-live": "polite" }), next);
  let seq = 0;
  // The list host and the pager are built once and only their contents change, so the pager keeps keyboard focus across a page change.
  const listHost = h("div");
  const errorHost = h("div");
  body.append(errorHost, listHost, nav);

  async function load() {
    const mine = ++seq;
    try {
      const r = parseHistory(await api<unknown>(`/v1/clients/${enc(ws.clientId)}/facts/history?limit=${PAGE}&offset=${offset}`));
      if (mine !== seq || !ws.life.alive()) return;
      entries = r?.entries ?? null;
      page = r?.page ?? null;
      loadError = r ? null : "The server sent a history this page does not understand.";
    } catch (e) {
      if (mine !== seq || !ws.life.alive()) return;
      loadError = describeProblem(e, "History could not be loaded").message;
    }
    paint();
  }
  async function go(next_: number) {
    if (next_ < 0 || (page && next_ >= page.total)) return;
    offset = next_;
    await load();
  }

  function entry(e: HistoryEntry): HTMLElement {
    return h(
      "li",
      { class: "fx-hist-item", dataset: { historyId: e.id, actor: e.actorKind } },
      h(
        "div",
        { class: "fx-unr-head" },
        h("strong", { dataset: { who: "true" } }, whoText(e.changedBy)),
        e.actorKind !== "other" ? pill(ACTOR_LABELS[e.actorKind], e.actorKind === "mcp" ? "warn" : "neutral") : null,
        e.identityVerified === false ? h("span", { class: "tag", title: "The server did not verify who made this change" }, "identity not verified") : null,
        h("span", { class: "muted" }, e.when ? e.when.text : "time unknown"),
        h("span", { class: "tag" }, e.source),
        e.kind === "created" ? h("span", { class: "tag", dataset: { kind: "created" }, title: "The facts the client was created with. Not a change: it is not counted in the revision." }, "created") : null,
      ),
      e.reason ? h("div", { class: "muted fx-small" }, `Reason: ${e.reason}`) : null,
      e.changes.length
        ? h("ul", { class: "fx-hist-changes" }, ...e.changes.map((c) => h("li", { dataset: { field: c.field } }, h("strong", null, fieldLabel(c.field)), ": ", h("span", { class: "fx-before" }, c.before), " → ", h("span", { class: "fx-after" }, c.after))))
        : h("div", { class: "muted fx-small" }, "No field changes recorded."),
    );
  }

  function paint() {
    const total = page?.total ?? entries?.length ?? 0;
    nav.hidden = !page || total <= PAGE;
    prev.setAttribute("aria-disabled", String(offset <= 0));
    next.setAttribute("aria-disabled", String(!page || offset + (entries?.length ?? 0) >= total));
    nav.querySelector("#fx-hist-range")!.textContent = entries && entries.length ? `${offset + 1}-${offset + entries.length} of ${total}` : "";
    errorHost.replaceChildren(...(loadError ? [h("div", { class: "error-banner", role: "alert" }, loadError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void load() } }, "Try again"))] : []));
    listHost.replaceChildren(
      ...(loadError
        ? []
        : [
            entries === null
              ? h("p", { class: "muted" }, "Loading…")
              : entries.length === 0
                ? h("p", { class: "muted" }, "No changes have been recorded since this client was created.")
                : h("ol", { class: "fx-hist-list", "aria-label": "Changes to approved facts, newest first" }, ...entries.map(entry)),
          ]),
    );
  }

  ws.onWrite(() => {
    offset = 0;
    void load();
  });
  void load();
  paint();
  return out;
}
